import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { drawnRemoteSrc, imgproxySrc } from '../../common/drawnImageSrc';
import { TypedMemo } from '../../common/types';
import { useGetMusicVideoEditorQuery } from '../../musicVideoEditor/musicVideoEditorApi';
import { formatTime } from '../../musicVideoEditor/timeline/timelineGeometry';
import { videoCreditsLeft } from '../../music24/promote/credits/videoCreditGate';
import { useVideoCredits } from '../../music24/promote/credits/useVideoCredits';
import { DIRECTED_WIZARD_STEPS, directedStepIndex } from '../directedSteps';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { directedMessages as messages } from '../messages/directedMessages';
import { DIRECTED_STEP_NAMES, DirectedStepper } from './DirectedStepper';
import { boldPart } from '../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

/** The size of the song cover in the top bar, in CSS px. */
const TOP_BAR_COVER_PX = 40;

/**
 * The song cover of the top bar through imgproxy, sized for the bar (owner rule 2026-10-19: the file
 * route streams the original, a 2.4 MB file that broke here). The source comes from the cached
 * editor answer (`artworkSourceUrl`); an older API answer keeps the address of the song.
 */
function topBarCoverSrc(
  editorSong: { artworkUrl: string | null; artworkSourceUrl?: string | null } | undefined,
  fallback: string | null
): string | null {
  const source = editorSong?.artworkSourceUrl;
  if (source !== null && source !== undefined && source !== '') {
    // A claimed cover has the same address as its source: imgproxy may fetch it only from an
    // allowed host. Our own file is always on the allow list (the file route of the API host).
    return source === editorSong?.artworkUrl
      ? drawnRemoteSrc(source, TOP_BAR_COVER_PX)
      : (imgproxySrc(source, TOP_BAR_COVER_PX) ?? fallback);
  }
  return fallback === null ? null : drawnRemoteSrc(fallback, TOP_BAR_COVER_PX);
}

/**
 * The top bar of the wizard (design DvTop; phone j): "Song" back, the song, the Beta chip, the
 * six-step stepper, Saved and the credits. On a phone: back, "Directed video", the step line and the
 * Beta chip; the step bars sit under it.
 */
const DirectedTopBarComponent: React.FC = () => {
  const intl = useIntl();
  const { accountId, catalogItemUuid, song, screen, leave } = useDirectedVideoContext();
  const { data: editor } = useGetMusicVideoEditorQuery({ accountId, catalogItemUuid });
  const coverSrc = topBarCoverSrc(editor?.song, song.artworkUrl);
  const { credits } = useVideoCredits({ accountId });
  const left = videoCreditsLeft(credits);
  const stepIndex = directedStepIndex(screen);
  const stepName = intl.formatMessage(DIRECTED_STEP_NAMES[DIRECTED_WIZARD_STEPS[stepIndex] ?? 'cast']);

  return (
    <header className={styles.top}>
      <button
        type="button"
        className={styles.back}
        onClick={leave}
        aria-label={intl.formatMessage(messages.backToSong)}
      >
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 5l-7 7 7 7" />
        </svg>
        <span>{intl.formatMessage(messages.back)}</span>
      </button>
      <div className={clsx(styles.song, styles.desktopOnly)}>
        {coverSrc !== null && <img src={coverSrc} alt="" />}
        <div className={styles.songText}>
          <b>{song.title}</b>
          <small>
            {song.durationSec === null
              ? intl.formatMessage(messages.songLineNoLength, { artist: song.artistName })
              : intl.formatMessage(messages.songLine, {
                  artist: song.artistName,
                  length: formatTime(song.durationSec),
                })}
          </small>
        </div>
      </div>
      <div className={clsx(styles.songText, styles.grow, styles.phoneOnly)}>
        <b>{intl.formatMessage(messages.phoneTitle)}</b>
        <small>
          {intl.formatMessage(messages.phoneStep, {
            title: song.title,
            number: stepIndex + 1,
            count: DIRECTED_WIZARD_STEPS.length,
            step: stepName,
          })}
        </small>
      </div>
      <span className={styles.beta}>{intl.formatMessage(messages.beta)}</span>
      <span className={clsx(styles.topSeparator, styles.desktopOnly)} />
      <div className={clsx(styles.grow, styles.desktopOnly)}>
        <DirectedStepper />
      </div>
      <span className={clsx(styles.saved, styles.desktopOnly)}>
        <i aria-hidden />
        {intl.formatMessage(messages.saved)}
      </span>
      {credits !== null && (
        <span className={clsx(styles.credits, screen !== 'price' && styles.desktopOnly)}>
          {left === null
            ? intl.formatMessage(messages.creditsNoLimit, { b: bold })
            : intl.formatMessage(messages.credits, { b: bold, count: left })}
        </span>
      )}
    </header>
  );
};

export const DirectedTopBar = TypedMemo(DirectedTopBarComponent);
